import React, { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppBar, styled, Grid } from '@mui/material';
import { Box } from '@mui/system';
import { StyledButton, Toast } from 'app/components';
import withAuth from 'app/components/authentication';
import { LanguageSwitcher, Loader } from 'app/components/v2';
import { CustomizedToolbar, IconsDiv } from './style';
import { ParentGrid, QrImageContainer } from './style';
import './style.css';
import { ColorsV2 } from 'app/constants/color';
import { routes } from 'app/routes';
import apiService from 'app/services/apiService';
import appService from 'app/services/appService';
import { useDispatch } from 'react-redux';
import { setOrgList } from 'app/store/features/orgSelectSlice';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [image, setImage] = useState('');
  const [loader, setLoader] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const { t } = useTranslation('translation', { keyPrefix: 'login_headings' });
  const [darkmode, setDarkmode] = useState(localStorage.getItem('theme') || false);
  const [showBankIdQRCode, setBankIdQRCode] = useState(false);

  useEffect(() => {
    setDarkmode(localStorage.getItem('theme'));
    document.documentElement.style.setProperty('--body-bg-color', ColorsV2.primary);
  }, [darkmode]);

  const CustomizedAppbar = styled(AppBar)({
    width: '100%',
    background: ColorsV2.primary,
    zIndex: 0,
    boxShadow: '0px 15px 10px -15px #111',
    marginTop: 'auto',
    marginBottom: 'auto',
    justifyContent: 'center',
  });

  const fetchUserData = async () => {
    try {
      const res = await apiService.getLoginData();
      localStorage.setItem('user', JSON.stringify(res));
      navigate(routes.ORGSELECT);
    } catch (error) {
      console.error(error);
      appService.logout();
      navigate(routes.LOGIN, {
        state: {
          message: 'Your Account is Locked!',
          severity: 'error',
        },
      });
    }
  };

  const fetchMicrosoftOIDCLogin = async () => {
    const res = await apiService.microsoftOIDCLogin();

    if (res?.data?.status === 'success') {
      window.location.href = res.data?.hint;
    } else {
      setToast({
        open: true,
        message: 'Login with Microsoft Failed!',
        severity: 'error',
      });
    }
  };

  const loginStart = flag => {
    // let ws = new WebSocket('');
    let ws = null;
    if (flag === 'open') {
      const { host, protocol } = window.location;
      const websocketUrl = `${
        // Protocol is checked to determine whether user is on production or local/dev environment and chooses a login endpoint accordingly
        protocol === 'https:' ? `wss://${host}/login` : `ws://ncpdev.nord-ic.com:8080/login`
      }`;
      ws = new WebSocket(websocketUrl);
      setLoader(true);
      ws.onmessage = function (event) {
        const messages = JSON.parse(event.data);
        if (messages.action === 'qrcode') {
          setImage(messages.value);
          setLoader(false);
        } else if (messages.action === 'authenticated') {
          const orgsObj = messages.organizations;
          if (orgsObj && Object.keys(orgsObj)?.length > 1) {
            const orgsArray = Object.entries(orgsObj).map(([id, name]) => ({
              id,
              name,
            }));
            dispatch(setOrgList(orgsArray));
          }

          const token = JSON.parse(messages.value);
          appService.setCookie(token.accessToken);
          fetchUserData();
        } else if (messages.action === 'failed') {
          setLoader(true);
          setTimeout(() => {
            setImage('');
            setBankIdQRCode(false);
            setLoader(false);
          }, 2000);
        }
      };
    } else {
      ws?.close(1000, 'Deliberate disconnection');
    }
  };

  const onMicrosoftButtonClick = () => {
    fetchMicrosoftOIDCLogin();
  };

  const gridStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    margin: '5vh',
  };

  const gridChildStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    margin: '1vh',
  };

  const instructionDivStyle = {
    width: '38vh',
    fontSize: '20px',
    textAlign: 'center',
    color: 'white',
    fontFamily: 'Poppins',
  };

  const textStyle = {
    textTransform: 'none',
    fontSize: '14px',
    fontFamily: 'Poppins',
  };

  const cancelDivStyle = {
    width: '36vh',
    fontSize: '18px',
    color: 'white',
    textDecoration: 'underline',
    textAlign: 'center',
    cursor: 'pointer',
    fontFamily: 'Poppins',
  };

  const onBankIdButtonClick = () => {
    setBankIdQRCode(true);
    loginStart('open');
  };

  const onCancelClick = () => {
    setBankIdQRCode(false);
    loginStart('close');
  };

  const resetToast = () => {
    setToast({
      open: false,
      message: '',
      severity: '',
    });
  };

  return (
    <>
      <ParentGrid container>
        <Grid xs={12} style={{ height: '4em' }}>
          <CustomizedAppbar elevation={0} position='fixed'>
            <CustomizedToolbar>
              <IconsDiv>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  fontFamily={'Roboto, sans-serif'}
                  width={'60%'}
                >
                  <img src={'../assets/images/Group-acent.svg'} alt='NIC logo' />
                </Box>
              </IconsDiv>
            </CustomizedToolbar>
          </CustomizedAppbar>
          <Box style={{ float: 'right', marginTop: '1vh' }}>
            <LanguageSwitcher />
          </Box>
        </Grid>

        <Grid xs={12} style={gridStyle}>
          <img
            src={'assets/images/NIC_LOGO.svg'}
            style={{ height: 'auto', maxWidth: '50%', width: '28%' }}
          />
        </Grid>

        {showBankIdQRCode ? (
          <>
            <Grid xs={12} style={gridChildStyle}>
              <QrImageContainer>
                <img
                  src={`data:image/png;base64,${image}`}
                  style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                />
              </QrImageContainer>
            </Grid>
            <Grid xs={12} style={gridChildStyle}>
              <div style={instructionDivStyle}>{t('helper_text.outstandingTransaction')}</div>
            </Grid>
            <Grid xs={12} style={gridChildStyle}>
              <div onClick={onCancelClick} style={cancelDivStyle}>
                {t('cancel')}
              </div>
            </Grid>
          </>
        ) : (
          <>
            <Grid xs={12} style={gridChildStyle}>
              <StyledButton
                onClick={onBankIdButtonClick}
                sx={{
                  color: 'white',
                  height: '7vh',
                  minHeight: '80px',
                  width: '33vh',
                  minWidth: '220px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginTop: '0px',
                  marginLeft: '5px',
                  fontWeight: '400',
                  backgroundColor: 'transparent',
                  border: 'solid 1px ' + ColorsV2.acent,
                  '&.MuiButton-root:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <img
                  src='/assets/images/Bank_ID_New.svg'
                  alt='BankID Logo'
                  style={{ maxHeight: '10vh', maxWidth: '10vh', marginRight: '2vh' }}
                />
                <div style={textStyle}>{t('signInWithBankId')}</div>
              </StyledButton>
            </Grid>

            <Grid xs={12} style={gridChildStyle}>
              <StyledButton
                onClick={onMicrosoftButtonClick}
                sx={{
                  color: 'white',
                  height: '7vh',
                  minHeight: '80px',
                  width: '33vh',
                  minWidth: '220px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginTop: '0px',
                  marginLeft: '5px',
                  fontWeight: '400',
                  backgroundColor: 'transparent',
                  border: 'solid 1px ' + ColorsV2.acent,
                  '&.MuiButton-root:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <img
                  src='/assets/images/Microsoft_Logo.svg'
                  alt='Microsoft Logo'
                  style={{
                    maxHeight: '5vh',
                    width: '6vh',
                    maxWidth: '8vh',
                    marginLeft: '2vh',
                    marginRight: '4vh',
                  }}
                />
                <div style={textStyle}>{t('signInWithMicrosoft')}</div>
              </StyledButton>
            </Grid>
          </>
        )}
      </ParentGrid>
      <Toast
        open={toast.open}
        setOpen={resetToast}
        message={toast.message}
        severity={toast.severity}
      />
      {loader && <Loader />}
    </>
  );
};

export default withAuth(Login);
